import i18next from 'i18next';

export const COMMON_COLUMNS = {
  ID: (isMainSheet) => ({
    name: i18next.t(
      isMainSheet ? 'GENERAL.ID' : 'ADMIN.RECIPES.COMPOSITION_EXPORT_RECIPE_ID_EXPORT',
    ),
    propertyKey: isMainSheet ? 'id' : 'recipeId',
  }),
  NAME: () => ({
    name: i18next.t('ADMIN.RECIPES.EXPORT_COLUMN_NAME'),
    propertyKey: 'name',
  }),
  STATUS: () => ({
    name: i18next.t('GENERAL.STATUS'),
    propertyKey: 'status',
    transform: (value) => i18next.t(`${value ? 'GENERAL.ACTIVE' : 'GENERAL.INACTIVE'}`),
  }),
  CATEGORY: (isMainSheet) => ({
    name: i18next.t(
      isMainSheet
        ? 'ADMIN.RECIPES.EXPORT_COLUMN_CATEGORY'
        : 'ADMIN.RECIPES.COMPOSITION_EXPORT_RECIPE_CATEGORY_EXPORT',
    ),
    propertyKey: 'category',
  }),
  ASSOCIATED_PRODUCT: () => ({
    name: i18next.t('ADMIN.RECIPES.EXPORT_COLUMN_ASSOCIATED_PRODUCT'),
    propertyKey: 'associatedProductName',
  }),
  QUANTITY: (isMainSheet) => ({
    name: i18next.t(
      isMainSheet
        ? 'ADMIN.RECIPES.EXPORT_COLUMN_QUANTITY'
        : 'ADMIN.RECIPES.COMPOSITION_EXPORT_RECIPE_QUANTITY_EXPORT',
    ),
    propertyKey: 'quantity',
  }),
  UNIT: (isMainSheet) => ({
    name: i18next.t(
      isMainSheet
        ? 'ADMIN.RECIPES.EXPORT_COLUMN_UNIT'
        : 'ADMIN.RECIPES.COMPOSITION_EXPORT_RECIPE_UNIT_EXPORT',
    ),
    propertyKey: 'unit',
  }),
};

export const AVAILABILITIES_COLUMNS = () => [
  {
    name: i18next.t('ADMIN.RECIPES.HAS_STOCK_LABEL'),
    propertyKey: 'hasStock',
    transform: (value) => i18next.t(`${value ? 'GENERAL.YES' : 'GENERAL.NO'}`),
  },
  {
    name: i18next.t('ADMIN.RECIPES.HAS_LOSS_LABEL'),
    propertyKey: 'hasLoss',
    transform: (value) => i18next.t(`${value ? 'GENERAL.YES' : 'GENERAL.NO'}`),
  },
  {
    name: i18next.t('ADMIN.RECIPES.HAS_TRANSFER_LABEL'),
    propertyKey: 'hasTransfer',
    transform: (value) => i18next.t(`${value ? 'GENERAL.YES' : 'GENERAL.NO'}`),
  },
];

export const ALLERGENS_COLUMN = () => ({
  name: i18next.t('ADMIN.INGREDIENTS.LABEL_ALLERGENS'),
  propertyKey: 'allergensNames',
  transform: (items) => items.join(','),
});

export const BRANDS_COLUMN = () => ({
  name: i18next.t('GENERAL.BRAND_PLURAL'),
  propertyKey: 'brands',
  transform: (values) => {
    const brandNames = values.map(({ name }) => name);

    return brandNames.join(',');
  },
});

// ----- Main Sheet - Single Channel Columns -----

export const DEFAULT_COST_COLUMN = () => ({
  name: i18next.t('GENERAL.COST_EX_TAX'),
  propertyKey: 'defaultCost',
  type: 'currency',
});

// ----- Main Sheet - Dynamic Per-Channel Columns -----

export const CHANNEL_RECIPE_COST_COLUMN = (channel) => ({
  name: i18next.t('ADMIN.RECIPES.COST_EX_TAX_CHANNEL', {
    channelName: i18next.t(`${channel.translationKey}.FULL`),
  }),
  propertyKey: `costByChannelId.${channel.id}`,
  type: 'currency',
});

export const CHANNEL_MARGIN_COLUMN = (channel) => ({
  name: i18next.t('ADMIN.RECIPES.EXPORT_COLUMN_MARGIN_CHANNEL', {
    channelName: i18next.t(`${channel.translationKey}.FULL`),
  }),
  propertyKey: `marginByChannelId.${channel.id}`,
  type: 'percentage',
});

// ----- Components Sheet - Static Columns -----

export const COMPONENT_COMPONENTS_ID_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_COMPONENT_ID_EXPORT'),
  propertyKey: 'componentId',
});

export const COMPONENT_NAME_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_COMPONENT_NAME_EXPORT'),
  propertyKey: 'componentName',
});

export const COMPONENT_TYPE_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_COMPONENT_TYPE'),
  propertyKey: 'componentType',
});

export const COMPONENT_CATEGORY_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_COMPONENT_CATEGORY_EXPORT'),
  propertyKey: 'componentCategory',
});

export const COMPONENT_QUANTITY_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_COMPONENT_QUANTITY_EXPORT'),
  propertyKey: 'componentQuantity',
});

export const COMPONENT_UNIT_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_COMPONENT_UNIT_EXPORT'),
  propertyKey: 'componentUnit',
});

export const COMPONENT_ALLERGENS_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_COMPONENT_ALLERGENS'),
  propertyKey: 'componentAllergensNames',
  transform: (items) => items.join(','),
});

// ----- Components Sheet - Single Channel Columns -----

export const COMPONENT_COST_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_COMPONENT_COST_EXPORT'),
  propertyKey: 'componentCost',
  type: 'currency',
});

export const RECIPE_DEFAULT_COST_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_RECIPE_COST_EXPORT'),
  propertyKey: 'defaultCost',
  type: 'currency',
});

// ----- Components Sheet - Dynamic Per-Channel Columns -----

export const CHANNEL_COMPONENT_USED_COLUMN = (channel) => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_COMPONENT_USED_CHANNEL', {
    channelName: i18next.t(`${channel.translationKey}.FULL`),
  }),
  propertyKey: `componentIsUsedByChannelId.${channel.id}`,
  transform: (value) => i18next.t(`${value ? 'GENERAL.YES' : 'GENERAL.NO'}`).toUpperCase(),
});

export const CHANNEL_COMPONENT_COST_COLUMN = (channel) => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_COMPONENT_COST_CHANNEL', {
    channelName: i18next.t(`${channel.translationKey}.FULL`),
  }),
  propertyKey: `componentCostByChannelId.${channel.id}`,
  type: 'currency',
});

// ----- Ingredients Sheet - Static Columns -----

export const INGREDIENT_ID_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_COMPONENT_ID_EXPORT'),
  propertyKey: 'componentId',
});

export const INGREDIENT_NAME_COLUMN = () => ({
  name: i18next.t('GENERAL.INGREDIENT'),
  propertyKey: 'componentName',
});

export const INGREDIENT_CATEGORY_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_INGREDIENT_CATEGORY_EXPORT'),
  propertyKey: 'componentCategory',
});

export const INGREDIENT_UNIT_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_INGREDIENT_UNIT_EXPORT'),
  propertyKey: 'componentUnit',
});

export const INGREDIENT_ALLERGENS_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_INGREDIENT_ALLERGENS'),
  propertyKey: 'componentAllergensNames',
  transform: (items) => items.join(','),
});

// ----- Ingredients Sheet - Single Channel Columns -----

export const INGREDIENT_QUANTITY_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_INGREDIENT_QUANTITY_EXPORT'),
  propertyKey: 'componentQuantity',
});

export const INGREDIENT_COST_COLUMN = () => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_INGREDIENT_COST_EXPORT'),
  propertyKey: 'componentCost',
  type: 'currency',
});

// ----- Ingredients Sheet - Dynamic Per-Channel Columns -----

export const CHANNEL_INGREDIENT_QUANTITY_COLUMN = (channel) => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_INGREDIENT_QUANTITY_CHANNEL', {
    channelName: i18next.t(`${channel.translationKey}.FULL`),
  }),
  propertyKey: `ingredientQuantityByChannelId.${channel.id}`,
});

export const CHANNEL_INGREDIENT_COST_COLUMN = (channel) => ({
  name: i18next.t('ADMIN.RECIPES.COMPOSITION_EXPORT_INGREDIENT_COST_CHANNEL', {
    channelName: i18next.t(`${channel.translationKey}.FULL`),
  }),
  propertyKey: `ingredientCostByChannelId.${channel.id}`,
  type: 'currency',
});
